import {
  Box,
  Button,
  Collapse,
  Divider,
  Flex,
  HStack,
  IconButton,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { Link } from "@components/ui-kit";
import { Trans, t } from "@lingui/macro";
import { Icon } from "@src/components/ui-kit/Icon";
import { currency } from "@src/utils/formatters";
import { observer } from "mobx-react-lite";
import { useState } from "react";
import type { TRelatedExpense } from "../../models/types";
import type { useStore } from "../../store";
import { RelatedDocumentsEditModal } from "./related-documents-edit-modal";

type Props = {
  readonly store: ReturnType<typeof useStore>;
};

export const RelatedDocuments = observer(function RelatedDocuments({
  store,
}: Props) {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!store.showRelatedDocuments) {
    return null;
  }

  const handleOpenModal = () => {
    if (!store.form.contractor_id.value) {
      store.appStore.UIStore.toast({
        status: "error",
        title: <Trans>Please select a contractor first</Trans>,
      });
      return;
    }

    store.relatedDocumentsModalStore.disclosureState.onOpen({
      selectedIds: store.form.related_expense_ids.value,
      contractorId: store.form.contractor_id.value,
      expenseType: store.form.type.value ?? store.expense?.type,
      onConfirm: (expenses: TRelatedExpense[]) => {
        store.form.related_expense_ids.onChange(expenses.map((e) => e.id));
        store.relatedExpenses = expenses;
      },
    });
  };

  return (
    <>
      <HStack
        cursor="pointer"
        onClick={() => setIsExpanded(!isExpanded)}
        rounded="md"
        spacing="2"
      >
        <Text fontSize="sm" fontWeight="medium" whiteSpace="nowrap">
          <Trans>Related documents</Trans>
          &nbsp;({store.relatedExpensesCount})
        </Text>

        <Divider />

        <IconButton
          aria-label={t`Toggle related documents`}
          colorScheme="grey"
          icon={
            isExpanded ? (
              <Icon name="chevron-up" />
            ) : (
              <Icon name="chevron-down" />
            )
          }
          size="sm"
          variant="ghost"
        />
      </HStack>

      <Collapse animateOpacity in={isExpanded}>
        <Box p="4">
          {store.relatedExpensesCount === 0 ? (
            <Text color="gray.500" fontSize="sm">
              <Trans>No related documents</Trans>
            </Text>
          ) : (
            <TableContainer>
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>
                      <Trans>Expense No</Trans>
                    </Th>
                    <Th>
                      <Trans>Contractor</Trans>
                    </Th>
                    <Th isNumeric>
                      <Trans>Amount</Trans>
                    </Th>
                    <Th w="0" />
                  </Tr>
                </Thead>
                <Tbody>
                  {store.relatedExpenses.map((expense) => (
                    <Tr key={expense.id}>
                      <Td>
                        <Link
                          href={{
                            pathname: "/expenses/edit",
                            query: { id: expense.id },
                          }}
                          target="_blank"
                        >
                          {expense.expense_no ?? expense.id}
                        </Link>
                      </Td>
                      <Td>
                        {expense.contractor
                          ? expense.contractor.legal_name
                          : "-"}
                      </Td>
                      <Td isNumeric>
                        {currency.formatByWorkspace(
                          expense.stats?.total_amount_with_vat_in_currency ?? 0,
                        )}
                      </Td>
                      <Td>
                        <IconButton
                          aria-label={t`Remove related document`}
                          colorScheme="red"
                          icon={<Icon name="trash-03" />}
                          onClick={() => store.removeRelatedExpense(expense.id)}
                          size="xs"
                          variant="ghost"
                        />
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          )}

          <Flex justify="end" w="full" mt="3">
            <Button
              leftIcon={<Icon name="pencil-01" />}
              onClick={handleOpenModal}
              size="sm"
              variant="ghost"
            >
              <Trans>Edit</Trans>
            </Button>
          </Flex>
        </Box>
      </Collapse>

      <RelatedDocumentsEditModal store={store.relatedDocumentsModalStore} />
    </>
  );
});
